import type {
  ICreateFileShareRequest,
  IDeleteFolderKnowledgeFileParams,
  IFolderKnowledgeFile,
  IKnowledgeAnalytics,
  IKnowledgeDownloadResponse,
  IKnowledgeFile,
  IKnowledgeFileShare,
  IKnowledgeQueryResponse,
  IKnowledgeShare,
  ISaveGeneratedFileToFolderParams,
  ISharedWithMeFile,
  IUploadFolderKnowledgeFileParams,
  IUploadKnowledgeFileParams,
  TEntityId,
} from "../types";
import type {
  IDownloadProgressEvent,
  IReactNativeFileUri,
} from "../types/chat";
import type { ApiClient } from "./client";

interface IDownloadFileContentParams {
  uuid: string;
  onProgress?: (event: IDownloadProgressEvent) => void;
}

export class KnowledgeApi {
  constructor(private readonly apiClient: ApiClient) {}

  // NOTE: React Native FormData.append() has extended behavior (not in the
  // standard DOM API): it accepts {uri, type, name} objects for file uploads,
  // but TypeScript's Blob type definition doesn't include this, so that branch
  // casts to bypass the limitation. Same pattern as `ChatApi.transcribeAudio`.
  private appendFile(
    formData: FormData,
    file: File | IReactNativeFileUri,
  ): void {
    if ("uri" in file) {
      formData.append("file", file as unknown as Blob);
      return;
    }
    formData.append("file", file);
  }

  async getFiles(): Promise<IKnowledgeFile[]> {
    const response =
      await this.apiClient.get<IKnowledgeFile[]>("/knowledge/files");
    return response.data;
  }

  async getFile(uuid: string): Promise<IKnowledgeFile> {
    const response = await this.apiClient.get<IKnowledgeFile>(
      `/knowledge/files/${uuid}`,
    );
    return response.data;
  }

  async uploadFile(
    params: IUploadKnowledgeFileParams,
  ): Promise<IKnowledgeFile> {
    const formData = new FormData();
    this.appendFile(formData, params.file);

    // NOTE: Do NOT set Content-Type manually — axios auto-sets it with the
    // correct multipart boundary and charset, which is required for non-ASCII
    // filenames (e.g. Cyrillic, Arabic, CJK) to be transmitted correctly.
    const response = await this.apiClient.post<IKnowledgeFile>(
      "/knowledge/files/upload",
      formData,
      {
        // NOTE: `signal` lets callers abort the in-flight POST via an
        // `AbortController`. axios surfaces the abort as a `CanceledError`
        // which the caller must catch — the leave-confirmation guard relies
        // on this path to actually cancel uploads on confirm.
        ...(params.signal && { signal: params.signal }),
        // NOTE: `onUploadProgress` powers the per-file progress bar in the
        // UI. axios polls XHR `progress` events; we emit a percentage so the
        // consumer doesn't need to recompute it for every event.
        ...(params.onProgress && {
          onUploadProgress: (event) => {
            if (!event.total) return;
            const percent = Math.round((event.loaded / event.total) * 100);
            params.onProgress?.(percent);
          },
        }),
      },
    );
    return response.data;
  }

  async retryFile(uuid: string): Promise<IKnowledgeFile> {
    const response = await this.apiClient.post<IKnowledgeFile>(
      `/knowledge/files/${uuid}/retry`,
    );
    return response.data;
  }

  async getDownloadUrl(uuid: string): Promise<IKnowledgeDownloadResponse> {
    const response = await this.apiClient.get<IKnowledgeDownloadResponse>(
      `/knowledge/files/${uuid}/download`,
    );
    return response.data;
  }

  async downloadFileContent(params: IDownloadFileContentParams): Promise<Blob> {
    const response = await this.apiClient.get<Blob>(
      `/knowledge/files/${params.uuid}/content`,
      {
        responseType: "blob",
        onDownloadProgress: params.onProgress
          ? (event) =>
              params.onProgress?.({
                loaded: event.loaded,
                total: event.total,
              })
          : undefined,
      },
    );
    return response.data;
  }

  async updateVisibility(props: {
    uuid: string;
    isPublic: boolean;
  }): Promise<IKnowledgeFile> {
    const response = await this.apiClient.patch<IKnowledgeFile>(
      `/knowledge/files/${props.uuid}/visibility`,
      { isPublic: props.isPublic },
    );
    return response.data;
  }

  async deleteFile(uuid: string): Promise<void> {
    await this.apiClient.delete(`/knowledge/files/${uuid}`);
  }

  async getFolderFiles(folderUuid: string): Promise<IFolderKnowledgeFile[]> {
    const response = await this.apiClient.get<IFolderKnowledgeFile[]>(
      `/knowledge/folders/${folderUuid}/files`,
    );
    return response.data;
  }

  async uploadFolderFile(
    params: IUploadFolderKnowledgeFileParams,
  ): Promise<IFolderKnowledgeFile> {
    const formData = new FormData();
    this.appendFile(formData, params.file);

    // NOTE: Do NOT set Content-Type manually — axios auto-sets the multipart
    // boundary (see uploadFile above for the non-ASCII filename rationale).
    const response = await this.apiClient.post<IFolderKnowledgeFile>(
      `/knowledge/folders/${params.folderUuid}/files/upload`,
      formData,
      {
        ...(params.signal && { signal: params.signal }),
        ...(params.onProgress && {
          onUploadProgress: (event) => {
            if (!event.total) return;
            const percent = Math.round((event.loaded / event.total) * 100);
            params.onProgress?.(percent);
          },
        }),
      },
    );
    return response.data;
  }

  async deleteFolderFile({
    folderUuid,
    uuid,
  }: IDeleteFolderKnowledgeFileParams): Promise<void> {
    await this.apiClient.delete(
      `/knowledge/folders/${folderUuid}/files/${uuid}`,
    );
  }

  async saveGeneratedFileToFolder({
    folderUuid,
    fileUuid,
  }: ISaveGeneratedFileToFolderParams): Promise<IFolderKnowledgeFile> {
    const response = await this.apiClient.post<IFolderKnowledgeFile>(
      `/knowledge/folders/${folderUuid}/files/from-generated/${fileUuid}`,
    );
    return response.data;
  }

  async getAnalytics(): Promise<IKnowledgeAnalytics> {
    const response = await this.apiClient.get<IKnowledgeAnalytics>(
      "/knowledge/analytics",
    );
    return response.data;
  }

  async query(queryText: string): Promise<IKnowledgeQueryResponse> {
    const response = await this.apiClient.post<IKnowledgeQueryResponse>(
      "/knowledge/query",
      { query: queryText },
    );
    return response.data;
  }

  async getShares(): Promise<IKnowledgeShare[]> {
    const response =
      await this.apiClient.get<IKnowledgeShare[]>("/knowledge/shares");
    return response.data;
  }

  async shareWithProject(targetProjectUuid: string): Promise<IKnowledgeShare> {
    const response = await this.apiClient.post<IKnowledgeShare>(
      "/knowledge/shares",
      { targetProjectUuid },
    );
    return response.data;
  }

  async unshareWithProject(targetProjectUuid: string): Promise<void> {
    await this.apiClient.delete(`/knowledge/shares/${targetProjectUuid}`);
  }

  async createFileShares(
    data: ICreateFileShareRequest,
  ): Promise<IKnowledgeFileShare[]> {
    const response = await this.apiClient.post<IKnowledgeFileShare[]>(
      "/knowledge/file-shares",
      data,
    );
    return response.data;
  }

  async getFileShares(fileUuid: string): Promise<IKnowledgeFileShare[]> {
    const response = await this.apiClient.get<IKnowledgeFileShare[]>(
      `/knowledge/file-shares/by-file/${fileUuid}`,
    );
    return response.data;
  }

  async getSharedWithMe(): Promise<ISharedWithMeFile[]> {
    const response = await this.apiClient.get<ISharedWithMeFile[]>(
      "/knowledge/file-shares/shared-with-me",
    );
    return response.data;
  }

  async removeFileShare(shareId: TEntityId): Promise<void> {
    await this.apiClient.delete(`/knowledge/file-shares/${shareId}`);
  }
}
